// First-load fix for the auth bridge.
//
// After the sign-in bounce-back (query "?src=si"), the back end has not yet been handed the
// user's token, so server-rendered content (e.g. the Benefits Snapshot pods) renders
// unauthenticated on the FIRST view — it would otherwise only populate on the next navigation.
// Once the bridge has primed the site-user cookie client-side, we reload ONCE so the server
// re-renders THIS page authenticated.
//
// Only the trigger param is stripped; any other query-string values already on the URL are
// preserved. Stripping the trigger also makes this one-shot (the reloaded page no longer
// matches the trigger, so it won't reload again).

const TRIGGER_PARAM = 'src';
const TRIGGER_VALUE = 'si';

/** True when the current URL carries the post-sign-in trigger (?src=si). */
export const isPostSignInReturn = (): boolean => {
	if (typeof window === 'undefined') {
		return false;
	}
	return new URLSearchParams(window.location.search).get(TRIGGER_PARAM) === TRIGGER_VALUE;
};

/**
 * True when the SERVER rendered THIS page with an established site-user session (the
 * `siteUserAuthenticated` bootstrap flag emitted by the header). If so, the page is already
 * authenticated and there's nothing to reload for.
 */
export const isServerSessionEstablished = (): boolean => {
	if (typeof window === 'undefined') {
		return false;
	}
	return (window as any).bootstrap?.siteUserAuthenticated === true;
};

/**
 * A reload is pending only when we returned from sign-in (?src=si) AND the server rendered this
 * page WITHOUT the site-user session yet. A returning user who was already authenticated
 * server-side (e.g. re-signing in with a live cookie) skips the needless reload.
 */
export const isReloadPending = (): boolean => isPostSignInReturn() && !isServerSessionEstablished();

/** The current URL with ONLY the trigger param removed (all other query values kept). */
export const urlWithoutTrigger = (): string => {
	const url = new URL(window.location.href);
	url.searchParams.delete(TRIGGER_PARAM);
	return url.toString();
};

/**
 * Reload with the trigger stripped so the server re-renders with the freshly primed cookie.
 * Uses replace() so the ?src=si URL doesn't linger in browser history.
 */
export const reloadWithoutTrigger = (): void => {
	if (typeof window === 'undefined') {
		return;
	}
	window.location.replace(urlWithoutTrigger());
};
